<template>
  <div>
    <wc-dialog
      v-if='taskCopy'
      :showDialog='showDialog'
      :title='!taskCopy.id ? "Create a new Task" : (taskCopy.event ? "Event" : "") + " Task Details"'
      :icon='$icons.tasks.icon'
      :disableButtons='!isFormValid'
      :loadingButtons='loading'
      @save='save'
      @cancel='cancel'
      width='400'
    >
      <v-form v-model='isFormValid' ref='form'>
        <v-row dense>
          <v-col cols='12' md='6'>
            <v-select
              dense
              label='Priority'
              :items='this.$store.state.misc.globalOptions.taskPriorities'
              v-model='taskCopy.priority'
              :hint="!canEdit.details ? canEdit.text : ''"
              :readonly='!canEdit.details'
              :filled='!canEdit.details'
            ></v-select>
          </v-col>
          <v-col cols='12'>
            <v-select
              dense
              label='Assigned to:'
              :rules='[rules.required]'
              :items='profiles'
              item-text='name_display'
              item-value='id'
              v-model='taskCopy.assigned_to'
              :hint="!canEdit.details ? canEdit.text : ''"
              :readonly='!canEdit.details'
              :filled='!canEdit.details'
            ></v-select>
          </v-col>
          <v-col cols='12'>
            <v-select
              dense
              label='Task Manager:'
              :items='profiles'
              item-text='name_display'
              item-value='id'
              v-model='taskCopy.task_manager'
              :hint="!canEdit.details ? canEdit.text : ''"
              :readonly='!canEdit.details'
              :filled='!canEdit.details'
              :append-outer-icon='taskCopy.task_manager ? "mdi-close" : ""'
              @click:append-outer='taskCopy.task_manager = null'
            ></v-select>
          </v-col>
          <v-col cols='12'>
            <wc-datepicker
              label='Date Due'
              v-model='taskCopy.date_due'
              :readonly='!canEdit.details'
              :filled='!canEdit.details'
              prepend-icon='mdi-calendar'
              :hint="!canEdit.details ? canEdit.text : '(optional)'"
              @change='changeTimeDue'
            ></wc-datepicker>
            <v-expand-transition>
              <wc-timepicker
                v-if='taskCopy.date_due'
                label='Time Due'
                :increment='15'
                :start='7'
                :end='17'
                clearable
                prependIcon='mdi-timer-outline'
                v-model='taskCopy.time_due'
                :readonly='!canEdit.details'
                :filled='!canEdit.details'
                :hint="!canEdit.details ? canEdit.text : '(optional)'"
              ></wc-timepicker>
            </v-expand-transition>
          </v-col>
          <v-col cols='12'>
            <v-textarea
              class='mt-2'
              dense
              label='Task Description'
              :rules='[rules.required]'
              v-model='taskCopy.description'
              outlined
              :persistent-hint='(!taskCopy.id && !!taskCopy.event)'
              :hint="!canEdit.details ? canEdit.text : (!taskCopy.id && taskCopy.event) ? 'copied in (can be edited without affecting event notes)' : ''"
              :readonly='!canEdit.details'
              :filled='!canEdit.details'
            ></v-textarea>
          </v-col>
          <v-expand-transition>
            <v-col cols='12' v-if='taskCopy.is_attempted || taskProgress == "Attempted"'>
              <v-textarea
                dense
                rows='2'
                label='Reason cannot complete'
                :rules='[rules.required]'
                v-model='taskCopy.notes'
                outlined
                hint="be descriptive so the manager of this task understands"
                persistent-hint
              ></v-textarea>
            </v-col>
          </v-expand-transition>
          <v-col cols='12'>
            <v-select
              v-if='taskCopy.id'
              :items='progressItems'
              menu-props="top"
              label="Mark as..."
              v-model='taskProgress'
              item-value="text"
            ></v-select>
          </v-col>
        </v-row>
      </v-form>

      <template v-slot:toolbarButtons>
        <v-chip :color='getStatus(taskCopy).color'>
          <v-icon left>{{ getStatus(taskCopy).icon }}</v-icon>
          {{ getStatus(taskCopy).title }}
        </v-chip>
      </template>

      <template v-slot:dialog-actions>
        <v-btn v-if='taskCopy.id' text small color='error' :disabled='!canEdit.details || loading' @click='showConfirmDelete = true'>
          Delete
        </v-btn>
      </template>
    </wc-dialog>

    <wc-confirm
      :showDialog='showConfirmDelete'
      @cancel='showConfirmDelete = false'
      @confirm='deleteTask'
    >
      <v-card-text>
        Are you sure you want to delete this Task?<br><br>
        <b>This cannot be undone</b>
      </v-card-text>
    </wc-confirm>
  </div>
</template>

<script>
// import moment from 'moment'
import formRules from '@/mixins/formRules'

export default {
  name: 'tasks-dialog',
  mixins: [formRules],
  props: {
    showDialog: {
      required: true,
      type: Boolean,
      default: false
    },
    loading: {
      required: false,
      type: Boolean,
      default: false
    },
    taskCopy: {
      // taskCopy must come from the parent as :taskcopy.sync="taskCopy"
      // this replaces our typical v-model (letting the parent keep control of the copy), and allows more readable code (using taskCopy instead of value)  
      required: true,
      type: Object
    },
    profiles: {
      required: true,
      type: Array
    },
  },
  data: () => ({
    isFormValid: false,
    showConfirmDelete: false,
    progressItems: [
      {text: 'In Progress'},
      {text: 'Attempted'}, 
      {text: 'Completed'}
    ],
    taskProgress: null
  }),
  watch: {
    showDialog() {
      if (this.showDialog === true) {
        if (this.taskCopy.is_complete) {
          this.taskProgress = "Completed"
        } else if (this.taskCopy.is_attempted) {
          this.taskProgress = "Attempted"
        } else if (this.taskCopy.in_progress) {
          this.taskProgress = "In Progress"
        } else {
          this.taskProgress = null
        }
        this.$nextTick(() => {
          this.$refs.form.resetValidation()
        })
      }
    }
  },
  computed: {
    canEdit() {
      if (this.taskCopy.is_complete) {
        return {details: false, text: "editing disabled (task complete)"}
      } else if (this.$route.path == "/tasks/dashboard" || this.$route.name == "OfficeCalendar") {
        return {details: true, text: ""}
      } else if (this.taskCopy.task_manager != this.$store.state.auth.profile.id && this.taskCopy.created_by != this.$store.state.auth.profile.id) {
        return {details: false, text: "only the manager/creator of this task can edit details"}
      } else if (this.taskCopy.event ) {
        return {details: false, text: "event task details can't be edited here"} 
      } 
      return {details: true, text: ""}
    }
  },
  methods: {
    // getPriority(item) {
    //   let tmp = this.$store.state.misc.globalOptions.taskPriorities.find(p => p.value === item.priority)
    //   if (tmp) {
    //     return tmp
    //   }
    //   return { text: "", value: null }
    // },
    getStatus (item) {
      if (!item.id) {
        return this.$icons.task_new
      } else if (!item.read) {
        return this.$icons.task_unread
      } else if (item.is_complete) {
        return this.$icons.task_complete
      } else if (item.is_attempted) {
        return this.$icons.task_attempted
      } else if (item.in_progress) {
        return this.$icons.task_progress
      } else {
        return this.$icons.task_read
      }
    },
    save(){
      if (this.taskProgress == "Completed") {
        this.taskCopy.is_complete = true
        this.taskCopy.completed_by = this.$store.state.auth.profile.id
        this.taskCopy.notes = ""
        this.taskCopy.is_attempted = false
        this.taskCopy.in_progress = false
      } else if (this.taskProgress == "Attempted") {
        this.taskCopy.is_complete = false
        this.taskCopy.completed_by = null
        this.taskCopy.is_attempted = true
        this.taskCopy.in_progress = false
      } else if (this.taskProgress == "In Progress") {
        this.taskCopy.is_complete = false
        this.taskCopy.completed_by = null
        this.taskCopy.notes = ""
        this.taskCopy.is_attempted = false
        this.taskCopy.in_progress = true
      }
      this.$emit('save')
    },
    cancel () {
      this.$emit('cancel')
    },
    deleteTask () {
      this.$emit('deleteTask')
      this.showConfirmDelete = false
    },
    changeTimeDue() {
      this.$nextTick(() => {
        if (!this.taskCopy.date_due) {
          this.taskCopy.time_due = null
        }
      })
    }
  }
}

</script>